<script lang="ts" setup>
import { ref, onMounted, watch, computed } from 'vue'
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useCartStore } from '@/stores/cart';
import { useProductStore } from '@/stores/product';
import { validateCart } from '@/api/cart';
import { useCountriesStore } from '@/stores/countries';
import { useStorage } from 'vue3-storage';
import messages from '@/locales/cart/messages';
import { useRouter } from 'vue-router';
import { InitiateCheckoutFB, genEventId } from '@/api/fb';

const { t, locale } = useI18n({
    useScope: 'global',
    messages: messages
})

const productStore = useProductStore()
const router = useRouter()
const storage = useStorage()
const countriesStore = useCountriesStore()
const cartStore = useCartStore()
const { cartAvailable, cart } = storeToRefs(cartStore)
const cartReturn = ref(null)
const cartReady = ref(false)
const shippingCost = ref(0);
const selectedShipping = ref(null);
const discountCode = ref('');

const cartQt = computed(()=>{
    if(cartReturn.value){
        return cartReturn.value.cart.reduce((acc, c)=>{
            return acc + c.qt
        },0)
    }
    return 0
})

const cartTotal = computed(()=>{
    if(cartReturn.value){
        return cartReturn.value.cart.reduce((acc, c)=>{
            return acc + c.qt * c.prices.discount
        },0)
    }
    return 0
})

const grandTotal = computed(()=>{
    let total = cartTotal.value + shippingCost.value
    if(cartReturn.value && cartReturn.value.discount && cartReturn.value.discount.result){
        total = total - cartReturn.value.discount.discount_ttc
    }
    return total
})
watch(cartAvailable, (newVal)=>{
    if(newVal){
        loadCart()
    }
})

function loadCart(){
    validateCart({cart:cart.value,discount_code:discountCode.value}).then((res)=>{
        cartReturn.value = res
        // out of cartReturn we can build the cart object , cartReturn.cart[].product_id, cartReturn.cart[].article_id, cartReturn.cart[].qt
        cart.value=cartReturn.value.cart.map((c)=>{
            return {
                product_id: c.product_id,
                article_id: c.article_id,
                qt: c.qt
            }
        })
        if(cartReturn.value.discount!=false && cartReturn.value.discount.result){
            updateCodeInStorage(discountCode.value)
        }
        cartReady.value = true
    }).catch((err)=>{
        console.log(err)
    })
}
function updateQt(event){
    // we update the qt of the article
    const article_id = event.id
    const qt = event.qt
    const index = cart.value.findIndex((c)=>c.article_id == article_id)
    if(index != -1){
        cart.value[index].qt = qt
    }
    loadCart()

}
function removeFromCart(event){
    // we remove the article from the cart
    const article_id = event
    const index = cart.value.findIndex((c)=>c.article_id == article_id)
    cartReady.value = false
    if(index != -1){
        
        cart.value.splice(index,1)
    }
    loadCart()
}
function updateCodeInStorage(code){
    storage.setStorage({key:'discount_code',data:code})
}
function discountCodeUpdated(code){
    discountCode.value = code;
    if(code==''){
        storage.removeStorage({key:'discount_code'})
    }
    loadCart();
}

function trackBeginCheckout() {
    return new Promise((resolve, reject)=> {
        // we get the cart items
        const cartItems = cartReturn.value?.cart.map(async (c: any) => {
            const res =  await productStore.getProductByArticleId(c.article_id)
            let article = res.articles.find((a: any) => a.id == c.article_id);
            return {
                name: res.name + ' - ' + article.name,
                article_name: res.name + ' - ' + article.name,
                article_id: article.sku,
                id: article.sku,
                price: c.prices.discount,
                quantity: c.qt
            };
        });
        Promise.all(cartItems).then((resolvedCartItems) => {
            console.log('cartItems', resolvedCartItems);
            if(window.dataLayer){
                window.dataLayer.push({ ecommerce: null }); // clear previous ecommerce object
                window.dataLayer.push({
                    event: 'begin_checkout',
                    eventCategory: 'Ecommerce',
                    eventAction: 'Start Checkout',
                    ecommerce: {
                        items: resolvedCartItems,
                        currency: 'EUR',
                        value: grandTotal.value,
                        shipping: shippingCost.value,
                    }
                });
            }
            // one shared event_id for CAPI + pixel so Meta deduplicates the two
            const eventId = genEventId();
            InitiateCheckoutFB(resolvedCartItems, eventId);
            if (window.fbq) {
                window.fbq('track', 'InitiateCheckout', {}, { eventID: eventId });
            }
            resolve();
        }).catch((error) => {
            console.error('Error resolving cart items:', error);
            reject(error);
        });
        /*
        if (window.consent.analytics_identified || window.consent.marketing_google || window.consent.marketing_facebook) {
            window.dataLayer.push({
            event: 'beginCheckout',
            eventCategory: 'Ecommerce',
            eventAction: 'Start Checkout',
            ecommerce: {
                items: cartItems
            }
            });
        }

        if (window.consent.marketing_facebook && window.fbq) {
            window.fbq('track', 'InitiateCheckout');
        }
        resolve()
        */
    })
}

function placeOrder(){
    // we go to the order page
    trackBeginCheckout().then(()=>{
        router.push({name:locale.value+'.order'})
    })
}

onMounted(()=>{
    // we check if we have a discount code in the storage
    const d_code = storage.getStorageSync('discount_code');
    if(d_code){
        discountCode.value = d_code
    }
    if(cartAvailable.value){
        loadCart()
    }
})
</script>
<template>
    <div class="cart_content">
        <div class="cart_detail">
            <div class="first-line">
                <h1>{{ $t('cart.title') }}</h1>
                <div class="num_articles">{{ $t('cart.num_article',cartQt) }}</div>
            </div>
            <div class="cart_header">
                <div class="cart_header_content">{{ $t('cart.products') }}</div>
                <div class="cart_header_content">{{ $t('cart.price') }}</div>
                <div class="cart_header_content">{{ $t('cart.qty') }}</div>
                <div class="cart_header_content">{{ $t('cart.total_inc_vat') }}</div>
            </div>
            <div class="cart_lines" v-if="cartReady">
                <cart-line v-for="article in cartReturn.cart" :key="article.id" :article="article" @update="updateQt" @delete="removeFromCart" />
            </div>
        </div>
        <div class="cart_recap">
            <h2>{{ $t('cart.recap') }}</h2>
            <template v-if="cartQt>0">

            
                <div class="articles_total">
                    <div class="articles_total_article">
                        {{ $t('cart.num_article',{count:cartQt}) }}
                    </div>
                    <div class="articles_total_price">
                        {{ cartTotal.toFixed(2).replace('.',',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ') }} €
                    </div>
                </div>
                <CartDiscount v-if="cartReturn" :discount="cartReturn.discount" @update="discountCodeUpdated" />
                <CartShipping v-if="cartReturn" :shippingCountries="cartReturn.countries" :userCountry="cartReturn.user_country" v-model="shippingCost" v-model:shipping="selectedShipping" />
                <div class="mt-2 w-100">
                    <label>{{ $t('cart.expedition_date') }}</label>
                    <div>{{ new Date(cartReturn.date_expe).toLocaleDateString(locale.toUpperCase() + '-' + locale.toUpperCase(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) }}</div>
                </div>
                <div class="grand_total">
                    <div class="grand_total_label">{{ $t('cart.total_inc_vat') }}</div>
                    <div class="grand_total_price">{{ grandTotal.toFixed(2).replace('.',',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ') }} €</div>
                </div>
                <z-button :disabled="selectedShipping===null" color="primary" @click="placeOrder" class="mt-8 w-100">{{ $t('cart.order') }}</z-button>
                <div class="mt-2"
                            data-pp-message
                            data-pp-style-layout="text"
                            data-pp-style-logo-type="inline"
                            data-pp-style-text-color="white"
                            data-pp-style-text-size="12"
                            data-pp-style-logo-position="left"
                            :data-pp-amount="grandTotal.toFixed(2)"
                            data-pp-currency="EUR"
                            data-pp-placement=cart>
                        </div>
            </template>
            <template v-else>
                <div class="articles_total">
                    <div class="articles_total_article">
                        {{ $t('cart.empty') }}
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>
<route lang="yaml">
name: cart
meta:
    title: Panier - KaseFilters
    layout: default
    description: Votre panier sur KaseFilters
    middleware: guest
</route>